import pdfMake, { TCreatedPdf } from 'pdfmake/build/pdfmake'

import { StringMap } from 'i18next'

import { FONTS } from './constants/constants'

import {
  CustomFields,
  SignatureUserProps,
  SiteProps,
  TestProps,
} from '@/@types/global'
import { ReportItems } from '@/@types/report/report-items'
import {
  AnalystProps,
  ContentProps,
  FieldMakePDFProps,
  GeneralContentProps,
  GeneralStylesProps,
  ReportValue,
} from '@/@types/template'
import { reportNumber } from '@/api/post'

pdfMake.fonts = FONTS

export const generatePdfFile = (documentDefinition: any): TCreatedPdf => {
  return pdfMake.createPdf(documentDefinition)
}

export const downloadPdfFile = async (
  documentDefinition: any,
  completeFileName: string
): Promise<void> => {
  return generatePdfFile(documentDefinition).download(completeFileName)
}

export const getPDFBase64 = async (
  documentDefinition: any
): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    try {
      generatePdfFile(documentDefinition).getBase64((cb) => {
        resolve(cb) // Corrigido: usar 'blob' em vez de 'buffer'
      })
    } catch (error) {
      reject(error)
    }
  })
}

export const getFilePDF = async (
  documentDefinition: any,
  completeFileName: string
): Promise<File> => {
  return new Promise<File>((resolve, reject) => {
    try {
      generatePdfFile(documentDefinition).getBlob((blob) => {
        resolve(new File([blob], completeFileName, { type: 'application/pdf' })) // Corrigido: usar 'blob' em vez de 'buffer'
      })
    } catch (error) {
      reject(error)
    }
  })
}

const getLayoutBody = (
  ignoreColor: number[],
  test: TestProps,
  colors: (string | null)[]
) => {
  const customRightBorder = {
    hLineWidth: (i, node) => {
      if (node.table.body[2][0].table) return 0
      return 1
    },
    vLineWidth: (i, node) => {
      if (node.table.body[2][0].table) return 0
      return 1
    },
  }

  return {
    ...(test.id === 'micro_cleanliness_d' ? customRightBorder : {}),
    fillColor: (rowIndex) => {
      if (test.id === 'microhardness' || test.id === 'chemical') {
        return colors[rowIndex]
      }
      return rowIndex % 2 === 0 &&
        rowIndex !== 1 &&
        rowIndex !== 0 &&
        !ignoreColor.includes(rowIndex)
        ? '#DCDCDC'
        : null
    },
  }
}

const getGeneralStyles = (generalContent: GeneralContentProps) => {
  return generalContent.generalStyles
}

const getLogo = (generalContent: GeneralContentProps) => {
  return generalContent.logo
}

const getGeneralLayoutPdf = (generalContent: GeneralContentProps) => {
  return generalContent.content
}

const getCorrectGeneralTest = (params: ContentProps, site: SiteProps) => {
  if (site.code === 'B') {
    return params.generalTestDetailB
  }
  if (site.code === 'J') {
    return params.generalTestDetailJ
  }
  return null
}

const getCorrectIdDoc = (params: ContentProps, site: SiteProps) => {
  if (site.code === 'B') {
    return params.idDocB
  }
  if (site.code === 'J') {
    return params.idDocJ
  }
  return null
}

const generateColors = (): (string | null)[] => {
  const colors = []
  for (let i = 0; i < 200; i++) {
    if (i % 2 === 0) {
      colors.push('#DCDCDC')
      colors.push('#DCDCDC')
    } else {
      colors.push(null)
      colors.push(null)
    }
  }
  return colors
}

const configRemark = (
  remark1: string | undefined,
  remark2: string | undefined,
  remark3: string | undefined
) => {
  let remarkDsc = ''
  if (remark1) {
    remarkDsc = remark1
  }
  if (remark2) {
    if (remarkDsc.length) {
      remarkDsc = `${remarkDsc}\n${remark2}`
    } else {
      remarkDsc = remark2
    }
  }
  if (remark3) {
    if (remarkDsc.length) {
      remarkDsc = `${remarkDsc}\n${remark3}`
    } else {
      remarkDsc = remark3
    }
  }
  return remarkDsc
}

export const getReportNumber = async (
  site: SiteProps,
  test: TestProps,
  numberTemporary: boolean
) => {
  const result = await reportNumber({
    site,
    test,
    numberTemporary,
  })

  return result.reportNumberValue
}

const getOrderItem = (sel) => {
  return `${sel.pedido ? sel.pedido.replace(/^0+(?!$)/g, '') : ''}/${sel.item ? sel.item : ''}`
}

const interpolate = async (
  params: ContentProps,
  report: ContentProps,
  generalContent: GeneralContentProps,
  generalStyles: GeneralStylesProps,
  logo: string,
  dateNow: {
    pt: string
    en: string
  },
  headerDetail: string,
  layout: any,
  approvalReport: FieldMakePDFProps[] | undefined,
  elaborationReport: FieldMakePDFProps[] | undefined,
  readerReport: FieldMakePDFProps[] | undefined,
  site: SiteProps,
  remark1: string | undefined,
  remark2: string | undefined,
  remark3: string | undefined,
  test: TestProps,
  reportNumberValue: string
) => {
  const remarkDsc = configRemark(remark1, remark2, remark3)

  // REPORT GENERAL DEFINITION
  report.styles = generalStyles
  report.content[0].table.body[0][0] = logo
  report.content[0].table.body[0][1].text = params.laboratoryName
  report.content[0].table.body[0][2].text[1].text = `${dateNow.pt} (${dateNow.en})`
  report.content[0].table.body[1][1].text = params.testName
  report.content[0].table.body[1][2].text[1].text = reportNumberValue
  if (test && ['four_point', 'hic', 'nace_d'].includes(test.id) && params.aco) {
    report.content[0].table.body[2][0].columns[0].text = `${report.content[0].table.body[2][0].columns[0].text}\nAQA (Steel chemistry):`
  }
  report.content[0].table.body[2][0].columns[1].text = headerDetail

  if (params.headerRows) {
    report.content[1].table.headerRows = params.headerRows
  } else if (params.headerRows === 0) {
    delete report.content[1].table.headerRows
  }
  report.content[1].layout = layout
  if (params.marginBodyDefinition) {
    report.content[1].margin = params.marginBodyDefinition
  }

  report.content[2].stack[0].columns[1].text = remarkDsc
  report.content[2].stack[4].text = getCorrectGeneralTest(params, site)

  let indexSignature = 0
  report.content[2].stack[6].columns[indexSignature].text = elaborationReport
  indexSignature++

  if (test.typeCode === 'corrosion' && readerReport) {
    report.content[2].stack[6].columns.splice(1, 0, { text: [] })
    report.content[2].stack[6].columns[indexSignature].text = readerReport
    indexSignature++

    report.content[2].stack[8].columns.push({
      style: 'smallCenter',
      text: '',
      width: 250,
      height: 100,
    })
    report.content[2].stack[9].columns.push({
      style: 'small',
      text: 'Assinatura (Signature)',
    })
  }

  report.content[2].stack[6].columns[indexSignature].text = approvalReport
  indexSignature++

  // report.content[8].stack[7].text = site.address;
  report.content[2].stack[13].text = params[`address${site.code}`]
  report.content[2].stack[14].text = site.phone
  report.content[2].stack[18].text = getCorrectIdDoc(params, site)

  const annexContent = JSON.parse(JSON.stringify(report.content))
  annexContent[0].table.body.splice(3, 1)
  annexContent[0].pageBreak = 'before'

  // REPORT SPECIFIC DEFINITION
  if (test.typeCode === 'collapse') {
    report.content[0].table.body.splice(3, 1)
  } else {
    report.content[0].table.body[3] = params.headerColumnsDefinition
  }

  if (params.bodyDefinitionWidths) {
    report.content[1].table.widths = params.bodyDefinitionWidths
  } else {
    delete report.content[1].table.widths
  }
  report.content[1].table.body = params.bodyDefinition
  if (test.id === 'micro_cleanliness_d') {
    report.content[1].margin = [-4, 0, 0, 0]
  }
  if (params.headerColumnsDefinitionDetail) {
    report.content[0].table.body.push(params.headerColumnsDefinitionDetail)
  }
  if (params.headerDataBookDefinition) {
    report.content[0].table.body.push(params.headerDataBookDefinition)
  }

  return { reportNumber: reportNumberValue, annex: annexContent }
}

export const addSignatureInDD = async (
  stack: any,
  email: string,
  column = 0,
  userSignature: SignatureUserProps,
  signatureBase64?: string
) => {
  if (stack && email) {
    const findUser =
      stack[6].columns[column].text &&
      stack[6].columns[column].text[1].text.trim() === ''
    if (!stack[8].columns[column].image || findUser) {
      if (
        stack[6].columns[column].text &&
        stack[6].columns[column].text[1].text.trim() === '' &&
        userSignature.name
      ) {
        stack[6].columns[column].text[1].text = userSignature.name
      }
      if (
        stack[6].columns[column].text &&
        stack[6].columns[column].text[3].text.trim() === '' &&
        userSignature.position
      ) {
        stack[6].columns[column].text[3].text = userSignature.position
      }
    }

    signatureBase64 && addSignatureWorkFlowS3(stack, signatureBase64, column)
  }
}

const addSignatureWorkFlowS3 = (toPlace: any, base64: string, column = 0) => {
  if (base64 && base64.length && !toPlace[8].columns[column].image) {
    const signElaboration = toPlace[8].columns[column]
    delete signElaboration.text
    signElaboration.image = base64
  }
}

export const getBase64FromUrl = async (url: string): Promise<string> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Erro ao buscar a imagem na URL fornecida')
  }
  const blob = await response.blob()

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        const base64WithPrefix = `data:${blob.type};base64,${reader.result.split(',')[1]}`
        resolve(base64WithPrefix)
      } else {
        reject(new Error('Erro ao converter imagem para Base64'))
      }
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export const getBase64FromUrlWithCompression = async (
  url: string,
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.7
): Promise<string> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Erro ao buscar a imagem na URL fornecida')
  }
  const blob = await response.blob()

  return new Promise<string>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      let { width, height } = img

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height)
        width *= ratio
        height *= ratio
      }

      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Erro ao criar contexto do canvas'))
        return
      }

      ctx.drawImage(img, 0, 0, width, height)

      const base64WithPrefix = canvas.toDataURL('image/jpeg', quality)
      resolve(base64WithPrefix)
    }

    img.onerror = reject
    img.src = URL.createObjectURL(blob)
  })
}

export const configGeneralParameters = (
  params: ContentProps,
  selected: ReportItems[],
  customFields: CustomFields,
  test: TestProps
) => {
  if (
    customFields &&
    customFields.customer &&
    customFields.customer.trim().length
  ) {
    params.client = customFields.customer.trim()
  } else {
    params.client =
      'desc_cliente' in selected[0] ? selected[0].desc_cliente : ''
  }

  if (
    customFields &&
    customFields.description &&
    customFields.description.trim().length
  ) {
    params.description = customFields.description.trim()
  } else {
    params.description =
      'descricao_mes' in selected[0] ? selected[0].descricao_mes : ''
  }

  if (
    customFields &&
    customFields.inspection &&
    customFields.inspection.trim().length
  ) {
    params.inspection = customFields.inspection.trim()
  } else {
    params.inspection =
      'firma_insp' in selected[0] ? selected[0].firma_insp : ''
  }

  if (
    customFields &&
    customFields.orderItem &&
    customFields.orderItem.trim().length
  ) {
    const orderItem = customFields.orderItem.trim().split('/')
    const selOrderItem = {
      pedido: orderItem[0],
      item: orderItem[1] ? orderItem[1] : '',
    }
    params.itemOrder = getOrderItem(selOrderItem)
  } else {
    params.itemOrder = getOrderItem(selected[0])
  }

  if (
    customFields &&
    customFields.project &&
    customFields.project.trim().length
  ) {
    params.project = customFields.project.trim()
  } else {
    params.project =
      'project' in selected[0] && selected[0].project
        ? selected[0]?.project
        : ' - '
  }

  if (
    test &&
    ['four_point', 'hic', 'nace_d', 'nace_d'].includes(test.id) &&
    'aco' in selected[0] &&
    selected[0].aco
  ) {
    params.aco = selected[0].aco
  }
}

export const getReaderReport = (
  generalContent: GeneralContentProps,
  analyst: AnalystProps[],
  crea: boolean
) => {
  generalContent.readerReport = [
    {
      style: 'boldSmall',
      text: 'Revisor (Checker): ',
    },
    {
      style: 'small',
      text: '{reportApproval}',
    },
    {
      style: 'boldSmall',
      text: '\nFunção (Position): ',
    },
    {
      style: 'small',
      text: '{reportFunction}',
    },
  ]
  const str = generalContent.readerReport
  let name = ''
  let func = ''
  if (analyst && analyst.length && analyst[0].name) {
    name = analyst[0].name
    func = analyst[0].description
  }
  if (crea) {
    str[1].text = func
    if (str[4]) str[4].text = '\nCREA:'
  } else {
    str[1].text = func
  }

  str[3].text = name
  return str
}

export const getGeneralReport = (
  generalContent: GeneralContentProps,
  analyst: AnalystProps[]
) => {
  const str = generalContent.generalReport
  let name = ''
  let func = ''

  if (analyst && analyst.length) {
    name = analyst[0].name
    func = analyst[0].description
  }
  str[1].text = func
  str[3].text = name

  return str
}

export const getApprovalReport = (
  generalContent: GeneralContentProps,
  analyst: AnalystProps[],
  crea: boolean
) => {
  const str = generalContent.approvalReport
  let name = ''
  let func = ''
  if (analyst && analyst.length) {
    name = analyst[0].name
    func = analyst[0].description
  }
  if (crea) {
    str[1].text = func
    if (str[4]) str[4].text = '\nCREA:'
  } else {
    str[1].text = func
  }
  str[1].text = func
  str[3].text = name
  return str
}

export const generateDocumentDefinitionSearch = (param: {
  dd: any
  version: string
  dateNow: {
    pt: string
    en: string
  }
  test: TestProps
  remark1: string | undefined
  remark2: string | undefined
  remark3: string | undefined
  generalContent: any
  elaboration: string
  elaborationPosition: string
  customFields: CustomFields
}) => {
  const {
    dd,
    version,
    dateNow,
    test,
    remark1,
    remark2,
    remark3,
    generalContent,
    elaboration,
    elaborationPosition,
    customFields,
  } = param
  const colors = generateColors()
  const pa = dd.ignore_color ? JSON.parse(dd.ignore_color) : []
  const layout = getLayoutBody(pa, test, colors)

  const documentDefinition = dd
  const parts = version.split('-R')
  const release = Number(parts[1]) + 1
  const newVersionName = `${parts[0]}-R${release}`

  const remarkDsc = configRemark(remark1, remark2, remark3)
  documentDefinition.content[2].stack[0].columns[1].text = remarkDsc
  documentDefinition.content[0].table.body[1][2].text[1].text = newVersionName
  documentDefinition.content[0].table.body[0][2].text[1].text = `${dateNow.pt} (${dateNow.en})`
  documentDefinition.content[1].layout = layout

  const generalReport = JSON.parse(JSON.stringify(generalContent.generalReport))

  generalReport[1].text = elaboration
  generalReport[3].text = elaborationPosition

  documentDefinition.content[2].stack[6].columns[0].text = generalReport

  let headerDetail = `${customFields.customer}\n${customFields.description}\n${
    customFields.inspection
  }\n${customFields.orderItem}`
  if (test.id === 'four_point' && customFields.aco) {
    headerDetail += `\n${customFields.aco}`
  }

  documentDefinition.content[0].table.body[2][0].columns[1].text = headerDetail

  return {
    documentDefinition,
    newVersionName,
  }
}

export const generateDocumentDefinition = async (
  params: ContentProps,
  dateNow: {
    pt: string
    en: string
  },
  generalContent: GeneralContentProps,
  site: SiteProps,
  remark1: string | undefined,
  remark2: string | undefined,
  remark3: string | undefined,
  test: TestProps,
  approvalReport: FieldMakePDFProps[] | undefined,
  elaborationReport: FieldMakePDFProps[] | undefined,
  readerReport: FieldMakePDFProps[] | undefined,
  reportNumberValue: string
): Promise<ReportValue> => {
  const report = getGeneralLayoutPdf(generalContent)
  const generalStyles = getGeneralStyles(generalContent)
  const logo = getLogo(generalContent)

  const headerDetail = `${params.client}\n${params.description}\n${params.inspection}\n${params.itemOrder} \n ${params.project} ${test && ['four_point', 'hic', 'nace_d'].includes(test.id) && params.aco ? `\n${params.aco}` : ''}`
  const ignoreColor = params.ignoreColor ? params.ignoreColor : []

  const colors = generateColors()
  const layout = params.layoutBody
    ? params.layoutBody
    : getLayoutBody(ignoreColor, test, colors)

  const reportNumberInterpolate = await interpolate(
    params,
    report,
    generalContent,
    generalStyles,
    logo,
    dateNow,
    headerDetail,
    layout,
    approvalReport,
    elaborationReport,
    readerReport,
    site,
    remark1,
    remark2,
    remark3,
    test,
    reportNumberValue
  )

  report.footer = function (currentPage: number, pageCount: number) {
    return [
      {
        text: `${currentPage.toString()} / ${pageCount}`,
        alignment: 'right',
        margin: [0, 0, 30, 0],
        style: 'small',
      },
      {
        text: [
          { style: 'boldSmall', text: 'Nº relatório (report n.): ' },
          { style: 'small', text: reportNumberValue },
        ],
        alignment: 'left',
        margin: [30, 0, 0, 0],
      },
    ]
  }

  return {
    documentDefinition: report,
    number: reportNumberValue,
    annex: reportNumberInterpolate.annex,
    headerDetailVal: headerDetail,
  }
}
